import React, { MutableRefObject, ReactNode, useCallback, useEffect, useMemo } from 'react';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Wizard, useWizard } from 'react-use-wizard';
import { useForm, FormProvider, SubmitHandler, useFormContext } from 'react-hook-form';
import { MapRef } from 'react-map-gl/maplibre';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Button, IconButton, Modal, ModalContent } from '@restworld/ui-ds-base';
import { Address, SaveResearchType } from '@restworld/utility-types';
import { useAuth } from '@restworld/feature-authentication';
import { useWorkersService } from '@restworld/data-services';
import {
  defaultItalyAddressMetaData,
  defaultItalyCoordinates,
  trackWorkerSavesTheResearch,
  useAnalytics,
  useFacebookPixel,
  useUTMSource,
} from '@restworld/utils-common';

import { saveResearchModalV2Atom } from '../../job-explorer-restaurant-explorer.atoms';
import {
  RegionalAreaSelection,
  AddressViewAndSelection,
  FilterSelection,
  NotificationUpdateSelection,
  ResearchSaveSuccessMessage,
} from './job-explorer-save-research-v2.components';
import { getWorkerAtom } from '../../../global-state.worker-data';
import { useJobExplorerFiltersFromContextValues } from '../../job-explorer-more-filter';
import { useKeepFocusedInputVisible } from './use-keep-focused-input-visible';
import { useHideIntercomLauncherOnMobile } from './use-hide-intercom-launcher-on-mobile';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IJobExplorerSaveResearchV2Props {
  prefillValues?: SaveResearchFormFields;
  startIndex?: number;
}
export interface SaveResearchFormFields extends SaveResearchType {
  seasonal?: string;
  address?: Partial<Address>;
  postal_code?: string;
  locality?: string;
  street_number?: string;
  modify_user_locality?: boolean;
  terms_and_conditions?: boolean;
  domain_interest?: string[];
  occupation_id?: { [x: string]: string[] };
}
export const areaSelectionAtom = atom<'whole_italy' | 'regional' | 'italy_and_abroad'>('regional');
export const toggleAddressEditViewAtom = atom(false);
export const saveResearchMapRefAtom = atom<MutableRefObject<MapRef>>({ current: null });
export const recordJobExplorerModalAtom = atom<boolean>(false);
const submitSaveResearchLoadingAtom = atom(false);
const showFooterButtonAtom = atom(true);
export const activeWizardStateAtom = atom(0);

export const JobExplorerSaveResearchV2 = ({ prefillValues, startIndex = 0 }: IJobExplorerSaveResearchV2Props) => {
  const [saveResearchModal, setSaveResearchModal] = useAtom(saveResearchModalV2Atom);
  useKeepFocusedInputVisible(saveResearchModal);
  useHideIntercomLauncherOnMobile(saveResearchModal);
  const areaSelection = useAtomValue(areaSelectionAtom);
  const showFooterButton = useAtomValue(showFooterButtonAtom);
  const methods = useForm<SaveResearchFormFields>({});
  const { defaultValues } = useDefaultValues();

  useEffect(() => {
    methods.reset(defaultValues);
  }, [defaultValues, methods]);

  useEffect(() => {
    methods.reset(prefillValues);
  }, [prefillValues, methods]);

  const { user } = useAuth();
  const { watch } = methods;
  const accomodation = watch('seasonal');
  const sector = watch('domain_interest');
  const qualification = watch('occupation_id');
  const { trackEvent } = useAnalytics();
  const activeWizardState = useAtomValue(activeWizardStateAtom);
  const recordActiveWizardState = useSetAtom(activeWizardStateAtom);
  const recordCloseEvent = useCallback(() => {
    trackEvent('save_search_job_details', {
      email: user?.email,
      origin: areaSelection,
      accomodation,
      sector: sector,
      qualification: qualification ? Object.values(qualification).flat().filter(Boolean) : undefined,
      close_button: true,
    });
  }, [accomodation, areaSelection, qualification, sector, trackEvent, user?.email]);
  return (
    <Modal
      open={saveResearchModal}
      onOpenChange={(v) =>
        !v &&
        (() => {
          methods.reset(defaultValues);
          setSaveResearchModal(false);
          recordCloseEvent();
          recordActiveWizardState(0);
        })()
      }
    >
      <ModalContent
        // Mobile: the address step has an input and a scrollable autocomplete list, so a swipe in
        // the body must scroll, never dismiss (the handle, the X and the overlay still close it).
        dismissOnBodyDrag={false}
        // Mobile height is a plain `height` (not min-height) so Vaul's inline height wins when it
        // shrinks the drawer above the on-screen keyboard; a min-height kept the drawer full size
        // and pushed its top (title, close button) off screen.
        className={`h-[85dvh] bg-white md:h-auto lg:max-h-fit ${
          // Step 0 (region cards) matches master's compact width. Master's BaseModal panel was
          // content-sized (min-w-104, no w-full) so `md:max-w-[52rem]` there was only a cap and the
          // panel rendered ~576px. ui-ds-base ModalContent forces w-full, so the same 52rem cap
          // expanded to the full 832px. Cap at xl (576px) to reproduce master's rendered width.
          activeWizardState === 0 ? 'md:max-w-xl' : 'md:max-w-lg'
        }`}
      >
        <FormProvider {...methods}>
          <form
            data-cy="save-research-form"
            // Mobile: fill the drawer body so the sticky footer (mt-auto) sits at the bottom even on
            // short steps.
            className="flex flex-col min-h-full md:block md:min-h-0"
            autoComplete="off"
            action="#!"
            method="post"
            onSubmit={(e) => e.preventDefault()}
          >
            <Wizard startIndex={startIndex} footer={<WizardFooter />}>
              <RegionalAreaSelection />
              <AddressViewAndSelection />
              <FilterSelection />
              <NotificationUpdateSelection />
              <ResearchSaveSuccessMessage />
            </Wizard>
          </form>
        </FormProvider>
      </ModalContent>
    </Modal>
  );
};

export default JobExplorerSaveResearchV2;
const WizardFooter = () => {
  const { activeStep } = useWizard();
  const addressEditView = useAtomValue(toggleAddressEditViewAtom);
  const [showFooterButton, setShowFooterButton] = useAtom(showFooterButtonAtom);
  const recordActiveWizardState = useSetAtom(activeWizardStateAtom);

  useEffect(() => {
    recordActiveWizardState(activeStep);
  }, [activeStep, recordActiveWizardState]);

  useEffect(() => {
    setShowFooterButton(activeStep !== 0 && !addressEditView);
  }, [activeStep, addressEditView, setShowFooterButton]);

  return showFooterButton ? (
    <div
      data-cy="save-research-footer-button"
      // Sticky inside the modal body scroller: always reachable above the keyboard, and the step
      // content scrolls behind it instead of being covered by a fixed overlay.
      // -bottom-4 / -mx-6 cancel the ModalContent body padding (px-6 py-4 in
      // libs/ui-ds-base/src/lib/modal/modal.tsx) so the footer spans the drawer edge to edge;
      // update them together if that padding changes.
      className="sticky -bottom-4 z-10 mt-auto -mx-6 bg-white px-6 pt-3 pb-6 md:static md:mx-0 md:w-full md:px-0 md:pt-0 md:pb-2"
    >
      <SubmitButton />
    </div>
  ) : null;
};
export const StepWrapper = ({ children }: { children: ReactNode }) => {
  const { activeStep, previousStep, goToStep } = useWizard();
  const areaSelection = useAtomValue(areaSelectionAtom);
  if (
    activeStep === 0 ||
    (activeStep === 2 && (areaSelection === 'whole_italy' || areaSelection === 'italy_and_abroad'))
  )
    return (
      <WrapperStyle activeStep={activeStep}>
        <div className="w-full pb-4 px-2 md:max-h-[calc(100svh_-_18rem)] md:overflow-y-auto md:pb-8">{children}</div>
      </WrapperStyle>
    );
  return (
    <>
      <WrapperStyle activeStep={activeStep}>
        {(activeStep === 2 || activeStep === 3) && (
          <IconButton
            // Match the modal's close button: a borderless, square icon button with a contained
            // hover. (Button variant="tertiary" drew a 1px border box and, with !px-0, a tall
            // narrow hover overlay.) ariaLabel kept English to match the DS close ("Close").
            Icon={ArrowLeftIcon}
            ariaLabel="Go back"
            size="md"
            onClick={() => {
              if (activeStep === 2 && areaSelection === 'whole_italy') {
                goToStep(0);
                return;
              }
              previousStep();
            }}
            wrapperStyle="md:absolute md:z-20 md:top-[18px] lg:top-[20px]"
          />
        )}
        {/* Mobile: no inner scroller, the modal body is the only one (nested scrollers fought with
            the drawer's drag handling); desktop keeps its capped inner scroll. */}
        <div className="w-full pb-4 px-2 md:max-h-[calc(100svh_-_18rem)] md:overflow-y-auto md:pb-8">{children}</div>
      </WrapperStyle>
    </>
  );
};
const WrapperStyle = React.forwardRef<HTMLDivElement, { activeStep?: number } & React.HTMLAttributes<HTMLDivElement>>(
  ({ activeStep: _activeStep, className, ...props }, ref) => <div ref={ref} className={className} {...props} />
);
const SubmitButton = () => {
  const wizardValues = useWizard();
  const activeStep = wizardValues?.activeStep;
  const nextStep = wizardValues?.nextStep;
  const { trackEvent, trackPHEvent } = useAnalytics();

  const { handleSubmit, watch, reset } = useFormContext<SaveResearchFormFields>();
  const workersService = useWorkersService();
  const { user } = useAuth();
  const { utmSource, refCode } = useUTMSource();
  const { defaultValues } = useDefaultValues();
  const [, setSaveResearchModal] = useAtom(saveResearchModalV2Atom);
  const loading = useAtomValue(submitSaveResearchLoadingAtom);
  const setLoading = useSetAtom(submitSaveResearchLoadingAtom);
  const areaSelection = useAtomValue(areaSelectionAtom);

  const { trackWorkerSavedResearch } = useFacebookPixel();
  const extractPayloadFromData = useCallback(
    (data: SaveResearchFormFields) => {
      const flattenOccupation = data.occupation_id ? Object.values(data.occupation_id).flat() : [];
      const occupationIds = flattenOccupation.filter(Boolean)?.length
        ? { occupation_id: flattenOccupation.filter(Boolean) }
        : {};
      const domainInterests = data.domain_interest?.length ? { domain_interest: data.domain_interest } : {};
      const seasonal = data.seasonal
        ? {
            contract_type: ['seasonal_accommodation'],
          }
        : {};
      const coordinates =
        areaSelection === 'regional'
          ? {
              ...(data.address_lat
                ? {
                    address_lat: data.address_lat,
                    address_lon: data.address_lon,
                    ...(data.area_distance ? { area_distance: data.area_distance * 1000 } : {}),
                  }
                : {
                    address_lon: defaultItalyCoordinates[0],
                    address_lat: defaultItalyCoordinates[1],
                    area_distance: 4000,
                  }),
              ...(data.area_distance ? { area_distance: data.area_distance * 1000 } : {}),
            }
          : {};
      const filters = {
        ...seasonal,
        ...domainInterests,
        ...occupationIds,
        ...(data.address && areaSelection === 'regional'
          ? { address: data.address }
          : areaSelection === 'whole_italy'
          ? {
              address: { ...defaultItalyAddressMetaData },
            }
          : {}),
      };
      const filterParams = {
        ...(Object.keys(filters).length ? { filter_params: filters } : {}),
      };
      const payload = {
        user_id: user?.id,
        frequency: data.frequency,
        preferred_location: areaSelection === 'whole_italy' ? ('italy' as const) : undefined,
        email: data.email,
        ...coordinates,
        ...filterParams,
        ...(typeof utmSource === 'string' && utmSource ? { source: utmSource } : {}),
        ...(refCode ? { ref_code: refCode } : {}),
      };
      return payload;
    },
    [areaSelection, user?.id, utmSource, refCode]
  );

  const { filterValues } = useJobExplorerFiltersFromContextValues();
  const router = useRouter();
  const onSubmit: SubmitHandler<SaveResearchFormFields> = useCallback(
    async (data) => {
      try {
        const payload = extractPayloadFromData(data);

        setLoading(true);
        await workersService.saveJPResearch(payload);
        if (!router.pathname.includes('/posizione'))
          filterValues({
            domain_interest: data.domain_interest,
            occupation_ids: data.occupation_id,
          });

        trackWorkerSavedResearch({
          user_id: payload.user_id,
          frequency: payload.frequency,
        });
        trackPHEvent('worker_submits_saved_research', {
          email: user?.email,
          origin: areaSelection,
          frequency: data.frequency,
          close_button: false,
        });
        trackWorkerSavesTheResearch();
        nextStep();
      } catch (error) {
        console.error(`error saving research ${error}`);
      } finally {
        setLoading(false);
      }
    },
    [
      areaSelection,
      extractPayloadFromData,
      filterValues,
      nextStep,
      router.pathname,
      setLoading,
      trackPHEvent,
      trackWorkerSavedResearch,
      user?.email,
      workersService,
    ]
  );
  const { t: jobExplorerTranslate } = useTranslation('job-explorer-page');
  const { t: commonTranslate } = useTranslation('common');
  const accomodation = watch('seasonal');
  const qualification = watch('occupation_id');
  const sector = watch('domain_interest');

  const buttonLabel = useMemo(
    () =>
      areaSelection === 'whole_italy' && activeStep === 2
        ? commonTranslate('continue')
        : activeStep === 4
        ? commonTranslate('i_understand')
        : activeStep === 3
        ? jobExplorerTranslate('save_research')
        : jobExplorerTranslate('save_research_continue_step', { step: activeStep }),
    [activeStep, areaSelection, commonTranslate, jobExplorerTranslate]
  );
  const handleClick = useCallback(() => {
    if (activeStep === 2) {
      trackEvent('save_search_job_details', {
        email: user?.email,
        origin: areaSelection,
        accomodation,
        sector: sector,
        qualification: qualification && Object.values(qualification).flat().filter(Boolean),
        close_button: false,
      });
    }
    if ([1, 2].includes(activeStep)) nextStep();
    if (activeStep === 3) handleSubmit(onSubmit)();
    if (activeStep === 4) {
      setSaveResearchModal(false);
      reset(defaultValues);
    }
  }, [
    accomodation,
    activeStep,
    areaSelection,
    defaultValues,
    handleSubmit,
    nextStep,
    onSubmit,
    qualification,
    reset,
    sector,
    setSaveResearchModal,
    trackEvent,
    user?.email,
  ]);

  return (
    <Button
      testId="save-research-continue-button"
      disabled={activeStep === 3 && !watch('terms_and_conditions')}
      loading={loading}
      fullwidth
      variant="primary"
      onClick={handleClick}
    >
      {buttonLabel}
    </Button>
  );
};
const useDefaultValues = () => {
  const workerData = useAtomValue(getWorkerAtom);
  const { user } = useAuth();
  const defaultValues = useMemo(
    () => ({
      email: user?.email,
      area_distance: 4,
      address_lon: workerData?.data?.address_lon,
      address_lat: workerData?.data?.address_lat,
      address: workerData?.data
        ? {
            address: workerData?.data.address,
            address_lat: workerData?.data.address_lat,
            address_lon: workerData?.data.address_lon,
            administrative_area_level_1: workerData?.data.administrative_area_level_1,
            administrative_area_level_2: workerData?.data.administrative_area_level_2,
            country: workerData?.data.country,
            locality: workerData?.data.locality,
            postal_code: workerData?.data.postal_code,
          }
        : undefined,
    }),
    [user?.email, workerData?.data]
  );
  return {
    defaultValues,
  };
};
